import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { isPension } from '@main/utils';

import { getDistinctPolicies } from './utils';

type CompanyKey = string;

export type CompanyResult = Record<CompanyKey, Policy[]>;

type PoliciesContextType = {
  externalIdToCollectionId: Record<string, string>;
  policies: CompanyResult;
  addPolicies: (params: {
    collectionId?: string;
    companyKey: CompanyKey;
    policies: Policy[];
  }) => void;
};

const PoliciesContext = createContext<PoliciesContextType | undefined>(undefined);

type PoliciesContextProviderProps = {
  children: React.ReactNode;
};

export function usePolicies() {
  const context = useContext(PoliciesContext);
  if (context === undefined) {
    throw new Error('usePolicies must be used within a PoliciesContextProvider');
  }
  return context;
}

export function useFlatPolicies() {
  const { policies } = usePolicies();
  return useMemo(() => Object.values(policies).flat(), [policies]);
}

export function PoliciesContextProvider({ children }: PoliciesContextProviderProps) {
  const [policies, setPolicies] = useState<CompanyResult>({});
  const [externalIdToCollectionId, setExternalIdToCollectionId] = useState<Record<string, string>>(
    {},
  );
  const addPolicies = useCallback(
    ({
      collectionId,
      companyKey,
      policies: newPolicies,
    }: {
      collectionId?: string;
      companyKey: CompanyKey;
      policies: Policy[];
    }) => {
      setPolicies((previousPolicies) => ({
        ...previousPolicies,
        [companyKey]: getDistinctPolicies(previousPolicies[companyKey] ?? [], newPolicies),
      }));
      if (!collectionId) return;
      setExternalIdToCollectionId((prevMapping) => ({
        ...prevMapping,
        ...newPolicies.reduce((mapping, policy) => {
          const externalId = isPension(policy) ? policy.externalId : policy.insurance.externalId;
          return { ...mapping, [externalId]: collectionId };
        }, {}),
      }));
    },
    [setPolicies],
  );

  const value = useMemo<PoliciesContextType>(
    () => ({
      externalIdToCollectionId,
      policies,
      addPolicies,
    }),
    [addPolicies, externalIdToCollectionId, policies],
  );

  return <PoliciesContext.Provider value={value}>{children}</PoliciesContext.Provider>;
}
